import {
  Box,
  Button,
  Collapsible,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import Link from "@pathwright/ux/src/components/link/Link"
import { useEffect, useRef, useState } from "react"
import { useClickAway, useMeasure } from "react-use"
import { UseMeasureResult } from "react-use/lib/useMeasure"
import { HomeRegistrationFragment } from "@pathwright/graphql-client/src/generated"
import Pathicon from "../../../pathicon/Pathicon"
import { getRegistrationPathLink } from "../../utils"
import PathCardListItem from "./PathCardListItem"
import PathCard from "./RegistrationPathCard"

function getMaxH(size: UseMeasureResult<HTMLDivElement>[1]) {
  // Setting scrollable container to be max height of first two items.
  return size.height ? `${size.height}px` : 0
}

type PathCardComponent = typeof PathCard | typeof PathCardListItem

type PathCardSharedProps<
  TPathCardComponent extends PathCardComponent = PathCardComponent
> = {
  registration: HomeRegistrationFragment
  nestedRegistrations: HomeRegistrationFragment[]
  PathCardComponent: TPathCardComponent
} & Parameters<TPathCardComponent>[0]

type PathCardStackProps = {
  sizeRef: UseMeasureResult<HTMLDivElement>[0]
  size: UseMeasureResult<HTMLDivElement>[1]
} & PathCardSharedProps

function PathCardStack({
  registration,
  nestedRegistrations,
  PathCardComponent,
  size,
  sizeRef,
  ...pathCardProps
}: PathCardStackProps) {
  const maxH = getMaxH(size)

  return (
    <VStack w="100%">
      <VStack w="100%" maxH={maxH} overflow="auto">
        <VStack ref={sizeRef} w="100%">
          {nestedRegistrations.slice(0, 2).map((nestedRegistration, i) => (
            <PathCardComponent
              key={nestedRegistration.id}
              registration={nestedRegistration}
              parentRegistration={registration}
              {...pathCardProps}
            />
          ))}
        </VStack>
        {nestedRegistrations.slice(2).map((nestedRegistration, i) => (
          <PathCardComponent
            key={nestedRegistration.id}
            registration={nestedRegistration}
            parentRegistration={registration}
            {...pathCardProps}
          />
        ))}
      </VStack>
      <Button asChild colorPalette="whiteAlpha" cursor="pointer" w="100%">
        <Link
          to={getRegistrationPathLink(registration)}
          w="100%"
          variant="plain"
        >
          View all
          <Pathicon icon="chevron-right" />
        </Link>
      </Button>
    </VStack>
  )
}

function ExpandablePathCard({
  registration,
  nestedRegistrations,
  PathCardComponent,
  ...pathCardProps
}: PathCardSharedProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  // Constraining the height of the scrollable stack to the first 2 elements.
  // Using useMeasure in parent component allows us to explicitly provide the
  // endingHeight to the Collapse component. Doing so avoids jank that occurs
  // when the Collapse opens, since it would have to wait for height of the child
  // component to be determined.
  const [sizeRef, size] = useMeasure<HTMLDivElement>()
  const maxH = getMaxH(size)

  const { open, onOpen, onClose } = useDisclosure()

  // Determine if discolosure was opened via hover, and if so,
  // we can allow for closing the disclosure on mouse leave.
  const [openedViaHover, setOpenedViaHover] = useState(false)
  useEffect(() => {
    if (!open) setOpenedViaHover(false)
  }, [open])

  // Close expanded PathCardStack when clicking outside.
  useClickAway(containerRef, onClose)

  // Disabling link on hoverless devices, allowing user to first
  // tap the PathCard to expand.
  const hasHover = window.matchMedia("(hover: hover)").matches
  const enableLink = hasHover || open

  return (
    <Box
      ref={containerRef}
      pos="relative"
      w="100%"
      zIndex={10}
      p={open ? 2 : 0}
      onClick={(e) => {
        if (!open) {
          // Prevent navigation, open top Cohort.
          // e.preventDefault()
          e.stopPropagation()
          onOpen()
        }
      }}
      onMouseOver={() => {
        if (!open) {
          onOpen()
          setOpenedViaHover(true)
        }
      }}
      onMouseLeave={() => {
        if (openedViaHover) {
          onClose()
        }
      }}
    >
      {open && (
        <Box pos="absolute" inset={0} bg="blackAlpha.400" borderRadius={25} />
      )}
      <VStack w="100%" pos="relative" zIndex={1}>
        <PathCardComponent
          registration={registration}
          nestedRegistrations={nestedRegistrations}
          enableLink={enableLink}
          {...pathCardProps}
        />
        <Collapsible.Root
          open={open}
          // NOTE: using unmountOnExit to prevent some inconsistency. If you collapse
          // and reopen to quickly, the Collapse component may not open again.
          unmountOnExit
          // endingHeight={maxH}
          // Seems Collapse doesn't support standard style props...
          style={{
            width: "100%"
          }}
        >
          <Collapsible.Content>
            <PathCardStack
              registration={registration}
              nestedRegistrations={nestedRegistrations}
              PathCardComponent={PathCardComponent}
              sizeRef={sizeRef}
              size={size}
              {...pathCardProps}
            />
          </Collapsible.Content>
        </Collapsible.Root>
      </VStack>
    </Box>
  )
}

function PathCardContainer({
  registration,
  nestedRegistrations,
  PathCardComponent,
  ...pathCardProps
}: PathCardSharedProps) {
  return nestedRegistrations.length ? (
    <ExpandablePathCard
      registration={registration}
      nestedRegistrations={nestedRegistrations}
      PathCardComponent={PathCardComponent}
      {...pathCardProps}
    />
  ) : (
    <PathCardComponent registration={registration} {...pathCardProps} />
  )
}

export default PathCardContainer
